<template lang="pug">
  v-card(v-if="form")
    v-card-title
      h2.modal-title {{ 'base.edit_ingredient' | translate }}
    v-card-content
      v-type-select(
        v-if="form.name !== 'H2O'"
        v-model="typeSearchText"
        :label="$t('base.type')"
        :hint="errorMessage('ingredient_type')"
        :invalid="!!errorMessage('ingredient_type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onTypeSelect"
        @create-enabled="onCreateTypeEnabled"
        @create-new="onCreateNew")
      v-input-r(
        ref="nameInput"
        v-if="form.name !== 'H2O'"
        :label="$t('base.name')"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        v-model="form.name")
      v-select.mb-2(
        v-if="form.name !== 'H2O'"
        :label="$t('base.stock')"
        :options="filteredStocks"
        v-model="form.stock.id")
      .toogle-wrap
        ui-toggle-unit(
          :tolerancePercent="form.tolerance_percent"
          :toleranceUnit="form.tolerance_unit"
          :toleranceKg="form.tolerance_kg"
          @update="onToleranceUpdate")
      v-input.mb-1(
        v-if="form.name !== 'H2O'"
        :label="$t('base.dry_metter')"
        :hint="errorMessage('dry_metter')"
        :float="true"
        :invalid="!!errorMessage('dry_metter')"
        type="text"
        pattern="^[0-9]+([.,][0-9]{0,2})?$"
        min="0"
        max="100"
        v-model="form.dry_matter")
      v-switch.mb-3(
        :label="$t('history.ignore_in_reports')"
        v-model="form.ignored_in_report")
      v-switch(
        v-if="form.name !== 'H2O'"
        :label="$t('history.autoload')"
        v-model="form.auto_load")
    v-card-actions
      v-btn(
        :loading="loading"
        @click="onSubmit")
        | {{ 'base.save' | translate }}
      delete-btn(
        v-if="form.name !== 'H2O'"
        @delete="onDelete")
</template>

<script>
import api from '@/api'
import { mapGetters, mapActions } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import UiToggleUnit from '@/components/ui/ui-toggle-unit/UiToggleUnit.vue'
import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'

export default {
  name: 'EditIngredientModal',

  props: {
    data: {
      type: Object,
      required: true,
      default: null
    }
  },

  mixins: [formErrorsMixin],

  components: {
    DeleteBtn,
    UiToggleUnit
  },

  data: () => ({
    form: null,
    loading: false,
    errors: {
      name: null,
      ingredient_type: null
    },
    typeSearchText: '',
    originalTypeName: '',
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false
  }),

  async mounted () {
    this.form = {
      ...this.data
    }

    if (this.form.ingredient_type && this.form.ingredient_type.name) {
      this.typeSearchText = this.form.ingredient_type.name
      this.originalTypeName = this.form.ingredient_type.name
      this.selectedType = this.form.ingredient_type
      this.createTypeEnabled = true
    }

    const company = +this.$route.params.company_id
    const farm = +this.$route.params.farm_id
    this.loadStocks({ company, farm })
    await this.loadIngredientTypes({ farm, limit: 2000 })
    await this.loadDefaultIngredientTypes()
  },

  computed: {
    ...mapGetters('stocks', ['stocks']),
    ...mapGetters('ingredienttypes', ['ingredientTypes', 'defaultIngredientTypes']),

    filteredStocks () {
      return this.stocks
        .filter(item => (item.farm === null &&
          (item.company && item.company.id) === +this.$route.params.company_id) ||
          (item.farm && item.farm.id) === +this.$route.params.farm_id)
    },

    filteredCustomTypes () {
      return this.ingredientTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.ingredientTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultIngredientTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    }
  },

  methods: {
    ...mapActions('stocks', ['loadStocks']),
    ...mapActions('ingredienttypes', ['loadIngredientTypes', 'loadDefaultIngredientTypes', 'createIngredientType']),

    onTypeSelect ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault

      this.form.name = type.name

      this.createTypeEnabled = true
      this.errors.ingredient_type = null

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    onCreateTypeEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
      }
      this.errors.ingredient_type = null
    },

    onCreateNew (typeName) {
      this.form.name = typeName

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    async onSubmit () {
      this.loading = true

      if (this.form.dry_matter && typeof this.form.dry_matter === 'string' && this.form.dry_matter.includes(',')) {
        this.form.dry_matter = this.form.dry_matter.toString().replace(',', '.')
      }

      this.checkValidData()

      if (this.errors.name === null && this.errors.ingredient_type === null) {
        let typeId = null

        if (this.form.ingredient_type && this.form.ingredient_type.id) {
          typeId = this.form.ingredient_type.id
        }

        const typeChanged = this.typeSearchText !== this.originalTypeName

        if (typeChanged) {
          const existingCustomType = this.ingredientTypes.find(t =>
            t.name.toLowerCase() === this.typeSearchText.toLowerCase()
          )

          if (!this.selectedType || this.isDefaultType || !existingCustomType) {
            this.creatingNewType = true
            const typePayload = {
              farm: +this.$route.params.farm_id,
              name: this.typeSearchText
            }
            const typeError = await this.createIngredientType(typePayload)

            if (typeError) {
              this.$toasted.error(this.$t('errors.incorrect_data'))
              this.loading = false
              this.creatingNewType = false
              return
            }

            const farm = +this.$route.params.farm_id
            await this.loadIngredientTypes({ farm })

            const newType = this.ingredientTypes.find(t =>
              t.name.toLowerCase() === this.typeSearchText.toLowerCase()
            )

            if (newType) {
              typeId = newType.id
            } else {
              this.$toasted.error(this.$t('errors.type_not_created'))
              this.loading = false
              this.creatingNewType = false
              return
            }

            this.creatingNewType = false
          } else if (this.selectedType) {
            typeId = this.selectedType.id
          }
        }

        const { id, ...payload } = this.form
        payload.ingredient_type = { id: typeId }

        const { errors } = await api.ingredients.updateById(id, payload)
        this.errors = errors

        if (!errors) {
          this.$emit('update')
          this.$emit('close')
        }
      }

      this.loading = false
    },

    checkValidData () {
      this.errors.name = null
      this.errors.ingredient_type = null

      if (!this.form.name) {
        this.errors.name = ['3']
      } else if (this.form.name.length > 100) {
        this.errors.name = ['6', 100]
      }

      if (!this.typeSearchText) {
        this.errors.ingredient_type = ['3']
      }
    },

    onToleranceUpdate (payload) {
      this.form.tolerance_kg = payload.tolerance_kg
      this.form.tolerance_percent = payload.tolerance_percent
      this.form.tolerance_unit = payload.tolerance_unit
    },

    async onDelete () {
      const { errors } = await api.ingredients.deleteById(this.form.id)
      if (!errors) {
        this.$emit('update', this.form.stock.id)
      }
      this.$emit('close')
    }
  }
}
</script>
<style scoped>
.v-card__title {
  padding: 2rem 2.4rem 1.6rem;
}

.modal-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.v-card__content {
  border-top: .1rem solid #d7d7d7;
  padding-bottom: 1.5rem !important;
}

.v-card__actions {
  padding: 0 2.4rem 2.4rem;
  display: flex;
  gap: 1rem;
}

.v-card__actions button:first-child {
  flex: 1;
}
</style>